/**
 * `size_ratio` — parsing and serialisation for the product forms' size string.
 *
 * One column (`store_listings.size_ratio varchar(64)`) carries two meanings,
 * disambiguated by `sold_by`:
 *
 *   - **pack** — the *composition*: how many pieces of each size make one
 *     bundle. `S:1,M:2,L:2,XL:1` is a 6-piece pack. Repeated per pack shape in
 *     `store_listings.packs` (ADR-0049).
 *   - **piece** — the *depth*: how many of each size the product is carried in.
 *     `S:10,M:20,L:20` totals 50 garments.
 *
 * Same `SIZE:N` grammar either way, so one parser reads one column; `sold_by`
 * is what says which question the numbers answer.
 *
 * Neither reading is stock, and neither touches money. `stock_balances` has no
 * size dimension (per-size inventory is V1.x), and the base price is per piece
 * regardless of what the totals say.
 *
 * Lifted out of `product-size-grid.tsx` when the pack grid became the second
 * consumer (AGENTS.md §7 rule 17).
 */
import { z } from "zod";

/**
 * Size run offered by the pickers. Matches `SZ_ALPHA` in
 * `packages/domain/src/garment/styles/templates.ts` so the store speaks the
 * same size vocabulary as the garment module.
 */
export const SIZES = ["S", "M", "L", "XL", "XXL"] as const;

/** `size_ratio` is varchar(64) — refuse rather than let Postgres truncate. */
export const MAX_SERIALISED = 64;

/** Matches `listingPacks` on the wire. A style with more runs is a modelling problem. */
export const MAX_PACKS = 12;

export type SizeCounts = Record<string, number>;

/** Which way an entry is sold. */
export const entryKind = z.enum(["piece", "pack"]);
export type EntryKind = z.infer<typeof entryKind>;

/**
 * One way to buy a product. Mirrors `storeCatalog.pricingEntry` (ADR-0051).
 *
 * Everything that varies between the ways a style is sold lives here — the
 * composition, price, discount and minimum. Only GST and the measure unit stay
 * on the product, because those are single columns the tax invoice reads.
 *
 * **This is a schema, not an interface, and both product forms use it as their
 * `pricingEntries` element.** They each held a private copy before, and both
 * copies omitted `packId`: a Zod object strips undeclared keys and `zodResolver`
 * hands react-hook-form the *parsed* values, so the pack link the picker had
 * just written was deleted on the way to the mutation and every priced save
 * came back a 400 (#196). One schema, and `PricingEntry` inferred from it, is
 * what makes that drift a compile error — drop a field here and the picker's
 * `onSave({ … })` literal stops type-checking.
 *
 * The form's shape is deliberately not the wire shape: `pricePaise` and
 * `discountBps` are always present here because the dialog always writes them,
 * where the contract has them optional for pre-ADR rows. `pricing-entry-form-schema.test.ts`
 * pins a parsed entry against `storeCatalog.pricingEntry` so the two can't drift.
 */
export const pricingEntryFormSchema = z.object({
  kind: entryKind,
  /**
   * The library pack this entry prices against (spec 13, R4). Every new entry
   * carries one — composition and pieces are read from it, never authored on
   * the entry. `kind` stays typed only so `entriesFromListing`'s legacy read of
   * pre-spec-13 rows still compiles; the picker only ever writes `"pack"`.
   *
   * Optional because a legacy row read off the wire may not have one; the
   * server refuses any entry that carries a price without it.
   */
  packId: z.string().uuid().optional(),
  label: z.string().trim().min(1).max(24),
  /** Pack: composition of one bundle. Piece: depth carried in each size. */
  sizeRatio: z.string().trim().max(64),
  /** Derived from `sizeRatio`, never typed. 1 for a piece. */
  pieces: z.number().int().min(0),
  /** Pack: price of the bundle. Piece: price of one garment. Integer paise. */
  pricePaise: z.string().trim(),
  /** Discount off this entry's price, basis points. */
  discountBps: z.number().int().min(0).max(10_000),
  /** Pack: minimum in packs. Piece: minimum in pieces. */
  moq: z.number().int().min(1).optional(),
  /**
   * How many of this pack the product has. Displayed, and deducted when an
   * order is fulfilled (ADR-0056) — but still not the stock ledger, which
   * lives in Inventory and is counted in pieces.
   * Not to be confused with the cart's `packQty`, which is what a buyer orders.
   *
   * Floor of 0 because fulfilment can empty a pack, and this schema parses
   * stored entries on the way *in* to the form — a floor of 1 would refuse to
   * open a sold-out entry for editing.
   */
  packsOnHand: z.number().int().min(0).optional(),
  /**
   * The bundle's colour(s), from the product's Color variant axis
   * (issue #235). Mirrors the wire fragment exactly — an overlay on the entry,
   * absent on every pre-#235 row.
   */
  colorSpec: z
    .object({
      mode: z.enum(["mixed", "single"]),
      colors: z.array(z.string().trim().min(1).max(48)).min(1).max(24),
    })
    .optional(),
  /** Per-variant breakdown behind a variant-composed bundle. Mirrors the wire. */
  variantComposition: z
    .array(
      z.object({
        attributeValues: z.record(z.string(), z.string()),
        qty: z.number().int().min(1).max(1000),
      }),
    )
    .max(100)
    .optional(),
});

export type PricingEntry = z.infer<typeof pricingEntryFormSchema>;

/**
 * An entry as it comes back from the wire, where everything added since the
 * column was introduced is optional — pre-ADR-0051 rows have no `kind`, and
 * pre-amendment ones no `pricePaise`.
 */
export type SavedEntry = Omit<PricingEntry, "kind" | "pricePaise" | "discountBps"> & {
  kind?: EntryKind | undefined;
  pricePaise?: string | undefined;
  discountBps?: number | undefined;
};

/**
 * Serialised form: `S:1,M:2,L:2,XL:1`, zeros omitted.
 *
 * Chosen over the legacy `1-2-2-1` because that format is positional — it only
 * means anything if you already know which sizes it covers, which is exactly
 * why it couldn't be rendered as a grid.
 */
export function serialiseSizes(counts: SizeCounts): string {
  return SIZES.filter((s) => (counts[s] ?? 0) > 0)
    .map((s) => `${s}:${counts[s]}`)
    .join(",");
}

/**
 * Parse a stored `size_ratio` back into counts.
 *
 * Returns `null` for anything that isn't our format — including the legacy
 * positional strings (`2-3-3-2-2`). The caller keeps such a value verbatim and
 * shows it rather than dropping it: silently replacing an unparsed run with an
 * empty grid would destroy data on the next save.
 */
export function parseSizes(raw: string | null | undefined): SizeCounts | null {
  if (!raw || !raw.trim()) return {};
  const counts: SizeCounts = {};
  for (const part of raw.split(",")) {
    const [size, qty] = part.split(":");
    const name = size?.trim().toUpperCase();
    const n = Number(qty);
    if (!name || !(SIZES as readonly string[]).includes(name)) return null;
    if (!Number.isInteger(n) || n < 0) return null;
    counts[name] = n;
  }
  return counts;
}

export function totalOf(counts: SizeCounts): number {
  return SIZES.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
}

/**
 * A library pack's composition serialised in **scale order** — `S:1,M:2,L:2,XL:1`.
 *
 * A pack's composition is a `{label → qty}` record whose key order isn't the
 * scale's, so the scale's ordered labels drive the string; labels the pack
 * doesn't ship are skipped, matching {@link serialiseSizes}. This is what the
 * picker writes onto an entry's `sizeRatio`, so the row/detail MetaPills read
 * left-to-right in the order buyers do.
 */
export function serialiseComposition(
  composition: Record<string, number>,
  scaleLabels: readonly string[],
): string {
  return scaleLabels
    .filter((l) => (composition[l] ?? 0) > 0)
    .map((l) => `${l}:${composition[l]}`)
    .join(",");
}

/**
 * Composition as ordered `label × qty` pairs — for the picker option and the
 * selected-pack MetaPills, which render a chip each.
 *
 * Parses the stored `sizeRatio` **order-preservingly** and, unlike
 * {@link parseSizes}, does not restrict labels to the alpha run: a tenant scale
 * is `28,30,32…` or `0-3m…` as easily as `S,M,L`, and the string is already
 * written in scale order (see {@link serialiseComposition}). A legacy positional
 * run (`1-2-2-1`, no `:`), or an empty string, yields no pairs.
 */
export function compositionPairs(sizeRatio: string): { label: string; qty: number }[] {
  const raw = sizeRatio?.trim();
  if (!raw) return [];
  const pairs: { label: string; qty: number }[] = [];
  for (const part of raw.split(",")) {
    const [label, qty] = part.split(":");
    const name = label?.trim();
    const n = Number(qty);
    if (!name || qty === undefined || !Number.isInteger(n) || n <= 0) return [];
    pairs.push({ label: name, qty: n });
  }
  return pairs;
}

/**
 * Composition as a compact one-liner — `S×1 M×2 L×2 XL×1` — for the picker
 * option. The `×` separator keeps numeric scales legible: `28×1 30×2` rather
 * than `281 302`, which reads as one number.
 */
export function compositionSummary(sizeRatio: string): string {
  return compositionPairs(sizeRatio)
    .map((p) => `${p.label}×${p.qty}`)
    .join(" ");
}

/**
 * Pieces in one pack — the composition total, falling back to a saved count.
 *
 * The grid is the source of truth (new-product-screen-spec.md R17: "its total
 * fills `pieces_per_pack`"). The fallback exists only for rows saved in the
 * legacy positional format (`1-2-2-1`), which `parseSizes` refuses: those carry
 * a real `pieces_per_pack` that must survive the next save rather than reset.
 *
 * Returns `0` when neither is usable, which the caller treats as "can't save
 * this as a pack" — the previous code coerced that case to `1` and quietly
 * stored a one-piece pack.
 */
export function effectivePiecesPerPack(
  sizeRatio: string,
  fallback: string | number | null | undefined,
): number {
  const parsed = parseSizes(sizeRatio);
  const total = parsed ? totalOf(parsed) : 0;
  if (total > 0) return total;
  const n = Number(fallback);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

/**
 * Entries worth keeping — the ones carrying a price.
 *
 * **Not** filtered on composition. The size grid is optional, so an entry may
 * legitimately have none, and filtering on it here would silently drop that
 * entry on save — a far worse outcome than the validation it replaced. Price is
 * the one field an entry cannot be meaningful without: it is what the catalog
 * quotes and what `items.default_rate_paise` derives from.
 */
// Generic in the element type, and structural in what it requires: the form's
// `PricingEntry` and the wire's looser row (optional `kind`/`pricePaise`) are
// both legal input, and each caller gets its own type back rather than being
// widened to the strictest one.
export function usableEntries<T extends { pricePaise?: string | undefined }>(
  entries: readonly T[],
): T[] {
  return entries.filter((e) => {
    try {
      return BigInt(e.pricePaise || "0") > 0n;
    } catch {
      return false;
    }
  });
}

/**
 * Whether a piece entry can still be added.
 *
 * At most one: you either sell the product loose or you don't, so a second
 * piece entry describes nothing new. Packs have no such limit — that is what
 * the composition-uniqueness rule is for.
 */
export function canAddPieceEntry(entries: readonly PricingEntry[], skipIndex?: number): boolean {
  return !entries.some((e, i) => i !== skipIndex && e.kind === "piece");
}

/**
 * A composition as readable text — `S 10 · M 15 · L 20`.
 *
 * For the preview cards, where the serialised `S:10,M:15,L:20` reads as a
 * machine value in a place the operator is scanning. A legacy positional run
 * can't be parsed, so it is shown verbatim rather than dropped.
 */
export function sizesOfferedLabel(sizeRatio: string): string {
  const counts = parseSizes(sizeRatio);
  if (counts === null) return sizeRatio;
  const parts = SIZES.filter((s) => (counts[s] ?? 0) > 0).map((s) => `${s} ${counts[s]}`);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

/** Sum of every entry's own price, in paise — the lot as quoted, before discount. */
export function totalEntryPricePaise(entries: readonly PricingEntry[]): bigint {
  return usableEntries(entries).reduce((sum, e) => sum + BigInt(e.pricePaise || "0"), 0n);
}

// Discounted per-entry pricing lives in `priceChainPaise` (`../price-math.ts`),
// not here. Two earlier helpers — `discountedEntryPricePaise` and its successor
// `entryNetPaise` — were each a partial copy of that rule, which is how the
// entry card, the products grid and the detail page ended up disagreeing about
// which figure a discount applies to (spec 13 R6a). One definition, three
// callers.

/** Pieces in the **primary** entry (`entries[0]`), or 0 if there isn't one. */
export function primaryEntryPieces(entries: readonly PricingEntry[]): number {
  return usableEntries(entries)[0]?.pieces ?? 0;
}

/**
 * Pieces this entry's quantity covers — `packsOnHand × pieces`, 0 when either
 * is unknown.
 *
 * Derived at every render rather than stored, so it cannot disagree with the
 * pack library the way a frozen copy would. It is a display figure only: no
 * stock is denominated in it (see `packsOnHand` on the contract).
 */
export function entryPiecesOnHand(entry: Pick<PricingEntry, "packsOnHand" | "pieces">): number {
  return (entry.packsOnHand ?? 0) * entry.pieces;
}

/** Pieces across every entry — one of each way to buy. */
export function totalEntryPieces(entries: readonly PricingEntry[]): number {
  return usableEntries(entries).reduce((sum, e) => sum + e.pieces, 0);
}

/**
 * What the products list says about a listing's availability (spec 13 R4c).
 *
 * Since fulfilment deducts `packsOnHand` (ADR-0056), a product can sell out —
 * or strand itself below its own minimum — with nobody typing a number. The
 * admin list showed neither: its Status column is *publish* state, so an empty
 * product read exactly like a full one.
 *
 * Two states rather than one, because they are different problems: `out-of-stock`
 * needs restocking, `below-minimum` has goods on the shelf that no buyer can
 * reach because the MOQ is now higher than what's left. Collapsing them would
 * hide the second, which is the harder one to notice.
 *
 * `in-stock` and `untracked` both render nothing — the list stays quiet so the
 * exceptions are what catch the eye, and grandfathered entries (written before
 * quantities were recorded) don't nag about data nobody entered.
 */
export type ListingStockState = "untracked" | "in-stock" | "below-minimum" | "out-of-stock";

/**
 * The three fields availability depends on — nothing else.
 *
 * Structural rather than the form's `PricingEntry` because the wire row leaves
 * `kind` and `pricePaise` optional, and both screens that draw this badge hold
 * the wire shape. Naming only what it reads also keeps the rule honest: an
 * entry's composition or label can never quietly start affecting stock.
 */
export interface StockEntry {
  pricePaise?: string | undefined;
  packsOnHand?: number | undefined;
  moq?: number | undefined;
}

/**
 * Is this entry orderable at its own minimum?
 *
 * **Mirrors `packUnorderable` in `apps/store/app/components/pack-option-card.tsx`**
 * — the storefront's rule for grinding a pack out. The two must agree, or the
 * admin will call a product available while the buyer sees UNAVAILABLE on the
 * same page. `apps/web` cannot import from `apps/store`, so the rule is restated
 * here and nowhere else; a third caller should lift it into `packages/domain`
 * rather than restate it again (AGENTS §5 rule 15).
 *
 * `packsOnHand === moq` is orderable — exactly the minimum is a legal order.
 */
function entryOrderable(entry: StockEntry): boolean {
  if (entry.packsOnHand === undefined) return true;
  return entry.packsOnHand >= (entry.moq ?? 1);
}

export function listingStockState(entries: readonly StockEntry[]): ListingStockState {
  // Only priced entries count: an unpriced one is not a way to buy, so its
  // quantity should never decide what the row says.
  const tracked = usableEntries(entries).filter((e) => e.packsOnHand !== undefined);
  if (tracked.length === 0) return "untracked";
  if (tracked.some(entryOrderable)) return "in-stock";
  // Nothing orderable. Which of the two it is turns on whether anything is
  // physically left, not on whether it can be sold.
  return tracked.every((e) => e.packsOnHand === 0) ? "out-of-stock" : "below-minimum";
}

/** Positional label for the entry at `index`, per kind. */
export function defaultEntryLabel(kind: EntryKind, index: number): string {
  return kind === "piece" ? "Piece" : `Pack ${index + 1}`;
}

/** True while the operator hasn't renamed it — the only labels safe to rewrite. */
export function isDefaultEntryLabel(label: string): boolean {
  return label === "Piece" || /^Pack \d+$/.test(label);
}

/**
 * The blank entry "Add bundle" opens with — only the positional label is
 * filled in.
 *
 * It briefly copied the last pack's composition and price, to save retyping a
 * size run. That was reverted: a dialog that opens holding the entry you just
 * saved reads as stale rather than as a template, and it fired the duplicate
 * warning before the operator had touched anything. Retyping the run is the
 * lesser cost.
 */
export function nextEntry(entries: readonly PricingEntry[], kind: EntryKind): PricingEntry {
  return {
    kind,
    label: defaultEntryLabel(kind, entries.length),
    sizeRatio: "",
    pieces: 0,
    pricePaise: "0",
    discountBps: 0,
  };
}

/**
 * The saved listing as pricing entries.
 *
 * Rows written before ADR-0051 have no `kind` — every one of them was a pack.
 * Rows written before entries existed at all describe their one way to buy
 * through `sold_by` + `size_ratio` + `pieces_per_pack`, so that is promoted to a
 * single entry. A legacy positional run (`1-2-2-1`) can't be split into sizes
 * and yields **no** entries — the form keeps the string on `sizeRatio` and shows
 * it, rather than inventing a composition.
 */
export function entriesFromListing(
  /** The wire shape, where everything added since the column is optional. */
  entries: readonly SavedEntry[] | null | undefined,
  soldBy: EntryKind | null | undefined,
  sizeRatio: string | null | undefined,
  piecesPerPack: number | null | undefined,
  /** Used only to price and discount an entry that carries neither of its own. */
  fallback?: { pricePaise?: string | null | undefined; discountBps?: number | null | undefined },
): PricingEntry[] {
  const usable = (entries ?? [])
    .filter((e) => e.sizeRatio.trim().length > 0 && e.pieces > 0)
    .map((e) => ({
      kind: e.kind ?? ("pack" as const),
      ...(e.packId !== undefined ? { packId: e.packId } : {}),
      label: e.label,
      sizeRatio: e.sizeRatio,
      pieces: e.pieces,
      pricePaise: e.pricePaise ?? fallback?.pricePaise ?? "0",
      discountBps: e.discountBps ?? fallback?.discountBps ?? 0,
      ...(e.moq !== undefined ? { moq: e.moq } : {}),
      ...(e.packsOnHand !== undefined ? { packsOnHand: e.packsOnHand } : {}),
      // Dropping a key here doesn't just hide it — the form re-saves entries
      // wholesale, so an unmapped field is silently ERASED on the next save.
      // That is exactly how #196 lost `packId`; colorSpec rides or dies here.
      ...(e.colorSpec !== undefined ? { colorSpec: e.colorSpec } : {}),
      ...(e.variantComposition !== undefined ? { variantComposition: e.variantComposition } : {}),
    }));
  if (usable.length > 0) return usable;

  const raw = sizeRatio?.trim() ?? "";
  const parsed = parseSizes(raw);
  const total = parsed ? totalOf(parsed) : 0;
  if (total > 0) {
    const kind: EntryKind = soldBy === "pack" ? "pack" : "piece";
    return [
      {
        kind,
        label: defaultEntryLabel(kind, 0),
        sizeRatio: raw,
        pieces: kind === "pack" ? (piecesPerPack ?? total) : 1,
        pricePaise: fallback?.pricePaise ?? "0",
        discountBps: fallback?.discountBps ?? 0,
      },
    ];
  }
  return [];
}
